import React, { useState, useEffect, useCallback } from 'react';
import { Box, Chip, Typography, IconButton, Grid, useTheme, useMediaQuery } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { 
  Add as AddIcon, 
  Edit as EditIcon, 
  Delete as DeleteIcon, 
  Email as EmailIcon, 
  Phone as PhoneIcon, 
  Business as BusinessIcon, 
  Payment as PaymentIcon,
  AccountBalance as AccountBalanceIcon,
  CreditCard as CreditCardIcon,
  Person as PersonIcon,
  Work as WorkIcon,
  ContentCopy as ContentCopyIcon,
  Build as BuildIcon,
} from '@mui/icons-material';

import ClientService from '../services/clientService';
import PageHeader from '../../../components/common/PageHeader';
import SearchHeader from '../../../components/common/SearchHeader';
import MetricsCard from '../../../components/common/MetricsCard';
import ResponsiveTable from '../../../components/common/ResponsiveTable';
import PaginationComponent from '../../../components/common/PaginationComponent';
import ClientActionDialog from '../../../components/common/ClientActionDialog';
import FormBuilder from '../../../components/common/FormBuilder';
import ItemCard from '../../../components/common/ItemCard';
import { createClientSchema, updateClientSchema } from '../schemas/clientFormSchema';

const ClientListingPage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Data states
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Statistics state
  const [statistics, setStatistics] = useState({
    total_clients: 0,
    total_credit: 0,
    total_paid: 0,
    balance: 0
  });
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [totalClients, setTotalClients] = useState(0);

  // Sorting states
  const [sortField, setSortField] = useState('balance');
  const [sortDirection, setSortDirection] = useState('desc');

  // Modal states
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);

  // Load clients function
  const loadClients = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = {
        skip: (currentPage - 1) * itemsPerPage,
        limit: itemsPerPage
      };
      
      // Server-side filtering
      if (searchTerm.trim()) {
        params.search = searchTerm.trim();
      }
      
      if (statusFilter === 'active') {
        params.is_active = true;
      } else if (statusFilter === 'inactive') {
        params.is_active = false;
      }
      
      // Server-side sorting
      params.sort_by = sortField;
      params.sort_order = sortDirection;
      
      const result = await ClientService.getAll(params);
      
      if (result.success) {
        setClients(result.data || []);
        setTotalClients(result.total || result.data?.length || 0);
      } else {
        setError(result.error);
        setClients([]);
        setTotalClients(0);
      }
    } catch (err) {
      console.error('Error loading clients:', err);
      setError('Σφάλμα φόρτωσης πελατών');
      setClients([]);
      setTotalClients(0);
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage, searchTerm, statusFilter, sortField, sortDirection]);

  // Load statistics
  const loadStatistics = useCallback(async () => {
    try {
      const params = {};
      
      if (statusFilter === 'active') {
        params.is_active = true;
      } else if (statusFilter === 'inactive') {
        params.is_active = false;
      }
      
      if (searchTerm.trim()) {
        params.search = searchTerm.trim();
      }
      
      const result = await ClientService.getStatistics(params);
      if (result.success) {
        setStatistics(result.data || {
          total_clients: 0,
          total_credit: 0,
          total_paid: 0,
          balance: 0
        });
      } else {
        console.error('Error loading statistics:', result.error);
        setStatistics({
          total_clients: 0,
          total_credit: 0,
          total_paid: 0,
          balance: 0
        });
      }
    } catch (err) {
      console.error('Error loading statistics:', err);
      setStatistics({
        total_clients: 0,
        total_credit: 0,
        total_paid: 0,
        balance: 0
      });
    }
  }, [searchTerm, statusFilter]);

  // Helper function for currency formatting
  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined || isNaN(amount)) {
      return '0,00 €';
    }
    return new Intl.NumberFormat('el-GR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  // Load data on mount and when filters change
  useEffect(() => {
    loadClients();
    loadStatistics();
  }, [loadClients, loadStatistics]);

  // Reset page when search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // Handle actions
  const handleSort = (field, direction) => {
    setSortField(field);
    setSortDirection(direction);
    setCurrentPage(1); // Reset to first page when sorting
  };

  const handleCreate = () => {
    setSelectedClient(null);
    setCreateModalOpen(true);
  };

  const handleEdit = async (client) => {
    try {
      const billingResult = await ClientService.getBillingInfo(client.id);
      setSelectedClient({
        ...client,
        billing_info: billingResult.success && billingResult.data ? billingResult.data : [],
      });
      setEditModalOpen(true);
    } catch (error) {
      console.error('Error loading billing info:', error);
      setSelectedClient({ ...client, billing_info: [] });
      setEditModalOpen(true);
      setError('Σφάλμα φόρτωσης φορολογικών στοιχείων');
    }
  };

  const handleDelete = (client) => {
    setSelectedClient(client);
    setDeleteDialogOpen(true);
  };

  const handleViewPayments = (client) => {
    try {
      const existingFilters = JSON.parse(localStorage.getItem('paymentListing.filters') || '{}');
      const updatedFilters = {
        paymentMethod: 'all',
        paymentType: 'all',
        client: String(client.id),
        ...existingFilters
      };
      localStorage.setItem('paymentListing.filters', JSON.stringify(updatedFilters));
      localStorage.setItem('paymentListing.paging', JSON.stringify({ page: 1, pageSize: 10 }));
      localStorage.setItem('paymentListing.sort', JSON.stringify({ field: 'payment_date', direction: 'desc' }));
    } catch (storageError) {
      console.error('Error preparing payment filters:', storageError);
    }

    navigate(`/payments?client=${client.id}`);
  };

  const handleViewServices = (client) => {
    try {
      const existingFilters = JSON.parse(localStorage.getItem('serviceListing.filters') || '{}');
      const updatedFilters = {
        clientStatus: 'all',
        paymentStatus: existingFilters.paymentStatus || 'all',
        serviceType: existingFilters.serviceType || 'all',
        subscription: existingFilters.subscription || 'all',
        expiration: existingFilters.expiration || 'all',
        status: existingFilters.status || 'all',
        client: String(client.id)
      };
      localStorage.setItem('serviceListing.filters', JSON.stringify(updatedFilters));
      localStorage.setItem('serviceListing.paging', JSON.stringify({ page: 1, pageSize: 10 }));
      localStorage.setItem('serviceListing.sort', JSON.stringify({ field: 'name', direction: 'asc' }));
    } catch (storageError) {
      console.error('Error preparing service filters:', storageError);
    }

    navigate(`/services?client=${client.id}`);
  };

  const handleCopyPublicLink = async (client) => {
    try {
      const response = await ClientService.getPublicToken(client.id);
      if (response.success) {
        const publicUrl = `${window.location.origin}/public/services/${response.data.token}`;
        await navigator.clipboard.writeText(publicUrl);
        setSuccess(`Ο σύνδεσμος αντιγράφηκε στο clipboard!`);
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError('Σφάλμα δημιουργίας συνδέσμου');
      }
    } catch (error) {
      console.error('Error copying public link:', error);
      setError('Σφάλμα αντιγραφής συνδέσμου');
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  const isMeaningfulBillingInfo = (billingInfo) => (
    Boolean(billingInfo?.vat_number || billingInfo?.business_name || billingInfo?.tax_office)
  );

  const mapBillingErrors = (result, index) => {
    const billingErrors = {};
    const errorDetail = result.errorResponse?.detail || result.error;

    if (Array.isArray(errorDetail)) {
      errorDetail.forEach((err) => {
        const fieldPath = err.loc || [];
        const fieldName = fieldPath[fieldPath.length - 1];
        if (fieldName) {
          const errorKey = `billing_info.${index}.${fieldName}`;
          let translatedMessage = err.msg;
          if (err.type === 'missing') {
            translatedMessage = 'Το πεδίο είναι υποχρεωτικό';
          } else if (err.type === 'string_type') {
            translatedMessage = 'Παρακαλώ εισάγετε έγκυρη τιμή';
          }
          billingErrors[errorKey] = translatedMessage;
        }
      });
    } else if (typeof errorDetail === 'string') {
      billingErrors[`billing_info.${index}.vat_number`] = errorDetail;
    }

    return billingErrors;
  };

  const syncClientBillingInfo = async (clientId, billingInfoList, existingBillingInfo = []) => {
    const meaningfulItems = (billingInfoList || []).filter(isMeaningfulBillingInfo);
    if (meaningfulItems.length === 0) {
      return { success: true };
    }

    const keptIds = new Set();
    const billingErrors = {};

    for (let index = 0; index < meaningfulItems.length; index++) {
      const billingInfo = meaningfulItems[index];
      let result;

      if (billingInfo.id) {
        const { id, client_id, created_at, updated_at, ...updateData } = billingInfo;
        result = await ClientService.updateBillingInfo(id, updateData);
        if (result.success) {
          keptIds.add(id);
        }
      } else {
        result = await ClientService.createBillingInfo(clientId, billingInfo);
        if (result.success && result.data?.id) {
          keptIds.add(result.data.id);
        }
      }

      if (!result.success) {
        Object.assign(billingErrors, mapBillingErrors(result, index));
      }
    }

    if (Object.keys(billingErrors).length > 0) {
      return {
        success: false,
        error: 'Παρακαλώ διορθώστε τα σφάλματα στα φορολογικά στοιχεία',
        errors: billingErrors,
      };
    }

    for (const existing of existingBillingInfo) {
      if (!keptIds.has(existing.id)) {
        await ClientService.deleteBillingInfo(existing.id);
      }
    }

    return { success: true };
  };

  // Form submission handlers
  const handleCreateSubmit = async (formData) => {
    try {
      // Extract billing_info from formData
      const billingInfoList = formData.billing_info || [];
      const clientData = { ...formData };
      delete clientData.billing_info; // Remove from client data
      
      // Create client first
      const result = await ClientService.create(clientData);
      if (result.success) {
        const clientId = result.data.id;
        const billingSync = await syncClientBillingInfo(clientId, billingInfoList);
        if (!billingSync.success) {
          return billingSync;
        }
        
        setCreateModalOpen(false);
        loadClients();
        return { success: true };
      } else {
        return { success: false, error: result.error };
      }
    } catch (error) {
      return { success: false, error: 'Σφάλμα δημιουργίας πελάτη' };
    }
  };

  const handleEditSubmit = async (formData) => {
    try {
      // Extract billing_info from formData
      const billingInfoList = formData.billing_info || [];
      const clientData = { ...formData };
      delete clientData.billing_info; // Remove from client data
      
      // Update client
      const result = await ClientService.update(selectedClient.id, clientData);
      if (result.success) {
        const existingBillingResult = await ClientService.getBillingInfo(selectedClient.id);
        const existingBillingInfo = existingBillingResult.success ? existingBillingResult.data : [];

        const billingSync = await syncClientBillingInfo(
          selectedClient.id,
          billingInfoList,
          existingBillingInfo
        );
        if (!billingSync.success) {
          return billingSync;
        }
        
        setEditModalOpen(false);
        loadClients();
        return { success: true };
      } else {
        return { success: false, error: result.error };
      }
    } catch (error) {
      return { success: false, error: 'Σφάλμα ενημέρωσης πελάτη' };
    }
  };

  const handleDeleteConfirm = async (actionType) => {
    try {
      let result;
      if (actionType === 'deactivate') {
        result = await ClientService.deactivate(selectedClient.id);
      } else {
        result = await ClientService.hardDelete(selectedClient.id);
      }
      
      if (result.success) {
        setDeleteDialogOpen(false);
        loadClients();
      } else {
        setError(result.error);
      }
    } catch (error) {
      setError(actionType === 'delete' ? 'Σφάλμα διαγραφής πελάτη' : 'Σφάλμα απενεργοποίησης πελάτη');
    }
  };

  // Mobile card renderer
  const renderClientCard = (client) => {
    const totalCredit = client.total_credit || 0;
    const balance = client.balance || 0;
    const isPositiveBalance = parseFloat(balance) >= 0;

    // Show credit and balance in basicInfo (first display)
    const basicInfo = [
      {
        icon: <CreditCardIcon fontSize="small" color="action" />,
        text: `Πιστωτικό: ${formatCurrency(totalCredit)}`
      },
      {
        icon: <AccountBalanceIcon fontSize="small" color={isPositiveBalance ? 'success' : 'error'} />,
        text: `Υπόλοιπο: ${formatCurrency(balance)}`
      }
    ];

    // Move email, phone, AFM, company to expandedContent
    const expandedContent = (
      <>
        {client.email && (
          <Box sx={{ mb: 1.5 }}>
            <Box display="flex" alignItems="center" gap={1}>
              <EmailIcon fontSize="small" color="action" />
              <Typography variant="body2" color="textSecondary">
                {client.email}
              </Typography>
            </Box>
          </Box>
        )}

        {client.phone && (
          <Box sx={{ mb: 1.5 }}>
            <Box display="flex" alignItems="center" gap={1}>
              <PhoneIcon fontSize="small" color="action" />
              <Typography variant="body2" color="textSecondary">
                {client.phone}
              </Typography>
            </Box>
          </Box>
        )}

        {client.vat_number && (
          <Box sx={{ mb: 1.5 }}>
            <Box display="flex" alignItems="center" gap={1}>
              <BusinessIcon fontSize="small" color="action" />
              <Typography variant="body2" color="textSecondary">
                ΑΦΜ: {client.vat_number}
              </Typography>
            </Box>
          </Box>
        )}

        {client.business_name && (
          <Box sx={{ mb: 1.5 }}>
            <Box display="flex" alignItems="center" gap={1}>
              <BusinessIcon fontSize="small" color="action" />
              <Typography variant="body2" color="textSecondary">
                {client.business_name}
              </Typography>
            </Box>
          </Box>
        )}

        {client.created_by_user && (
          <Box sx={{ mb: 1.5, mt: 1 }}>
            <Box display="flex" alignItems="center" gap={1}>
              <PersonIcon fontSize="small" color="action" />
              <Typography variant="body2" color="textSecondary">
                Δημιουργός: {client.created_by_user.name}
              </Typography>
            </Box>
          </Box>
        )}
      </>
    );

    return (
      <ItemCard
        key={client.id}
        title={client.name}
        subtitle={null}
        headerActions={[
          <Chip 
            key="status"
            label={client.is_active ? 'Ενεργός' : 'Ανενεργός'}
            color={client.is_active ? 'success' : 'default'}
            size="small"
          />,
          <Chip 
            key="services"
            label={`${client.services_count || 0} υπηρεσίες`}
            color="info"
            variant="outlined"
            size="small"
          />
        ]}
        basicInfo={basicInfo}
        expandedContent={expandedContent}
        actions={[
          <IconButton
            key="copy-link"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleCopyPublicLink(client);
            }}
            sx={{ color: 'info.main' }}
            title="Αντιγραφή Συνδέσμου Υπηρεσιών"
          >
            <ContentCopyIcon fontSize="small" />
          </IconButton>,
          <IconButton
            key="services"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleViewServices(client);
            }}
            sx={{ color: 'secondary.main' }}
            title="Προβολή Υπηρεσιών"
          >
            <BuildIcon fontSize="small" />
          </IconButton>,
          <IconButton
            key="payments"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleViewPayments(client);
            }}
            sx={{ color: 'success.main' }}
            title="Προβολή Πληρωμών"
          >
            <PaymentIcon fontSize="small" />
          </IconButton>,
          <IconButton
            key="edit"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleEdit(client);
            }}
            sx={{ color: 'primary.main' }}
          >
            <EditIcon fontSize="small" />
          </IconButton>,
          <IconButton
            key="delete"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleDelete(client);
            }}
            sx={{ color: 'error.main' }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        ]}
      />
    );
  };

  // Table columns configuration
  const columns = [
    {
      field: 'name',
      headerName: 'Όνομα',
      sortable: true,
      minWidth: 200,
      renderCell: (value) => (
        <Typography variant="body2" fontWeight={500}>
          {value}
        </Typography>
      )
    },
    {
      field: 'email',
      headerName: 'Email',
      sortable: true,
      minWidth: 200,
    },
    {
      field: 'phone',
      headerName: 'Τηλέφωνο',
      sortable: false,
      minWidth: 150,
    },
    {
      field: 'vat_number',
      headerName: 'ΑΦΜ',
      sortable: false,
      minWidth: 120,
      renderCell: (value) => (
        <Typography variant="body2" color="text.secondary">
          {value || '—'}
        </Typography>
      )
    },
    {
      field: 'business_name',
      headerName: 'Επωνυμία',
      sortable: false,
      minWidth: 180,
      renderCell: (value) => (
        <Typography variant="body2" color="text.secondary">
          {value || '—'}
        </Typography>
      )
    },
    {
      field: 'services_count',
      headerName: 'Υπηρεσίες',
      sortable: true,
      minWidth: 120,
      renderCell: (value) => (
        <Chip
          label={`${value || 0} υπηρεσίες`}
          color="info"
          variant="outlined"
          size="small"
        />
      )
    },
    {
      field: 'total_credit',
      headerName: 'Πιστωτικό',
      sortable: true,
      minWidth: 150,
      renderCell: (value, row) => (
        <Typography variant="body2" fontWeight="medium">
          {formatCurrency(row.total_credit || 0)}
        </Typography>
      )
    },
    {
      field: 'balance',
      headerName: 'Υπόλοιπο',
      sortable: true,
      minWidth: 150,
      renderCell: (value, row) => {
        const balance = row.balance || 0;
        const isPositive = parseFloat(balance) >= 0;
        return (
          <Typography 
            variant="body2" 
            fontWeight="medium"
            color={isPositive ? 'success.main' : 'error.main'}
          >
            {formatCurrency(balance)}
          </Typography>
        );
      }
    },
    {
      field: 'is_active',
      headerName: 'Κατάσταση',
      sortable: true,
      minWidth: 120,
      renderCell: (value) => (
        <Chip
          label={value ? 'Ενεργός' : 'Ανενεργός'}
          color={value ? 'success' : 'default'}
          size="small"
        />
      )
    },
    {
      field: 'actions',
      headerName: 'Ενέργειες',
      sortable: false,
      minWidth: 100,
      renderCell: (value, row) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton
            size="small"
            onClick={() => handleCopyPublicLink(row)}
            sx={{ color: 'info.main' }}
            title="Αντιγραφή Συνδέσμου Υπηρεσιών"
          >
            <ContentCopyIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleViewServices(row)}
            sx={{ color: 'secondary.main' }}
            title="Προβολή Υπηρεσιών"
          >
            <BuildIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleViewPayments(row)}
            sx={{ color: 'success.main' }}
            title="Προβολή Πληρωμών"
          >
            <PaymentIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleEdit(row)}
            sx={{ color: 'primary.main' }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleDelete(row)}
            sx={{ color: 'error.main' }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      )
    }
  ];


  // Page header actions
  const pageActions = [
    <IconButton
      key="create"
      color="primary"
      onClick={handleCreate}
      sx={{ 
        backgroundColor: 'primary.main',
        color: 'white',
        '&:hover': {
          backgroundColor: 'primary.dark',
        }
      }}
    >
      <AddIcon />
    </IconButton>
  ];

  return (
    <Box sx={{ p: 3 }}>
      {/* Page Header */}
      <PageHeader
        title="Διαχείριση Πελατών"
        subtitle="Προβολή και διαχείριση όλων των πελατών"
        icon="👥"
        actions={pageActions}
      />

      {/* Statistics Cards */}
      <Grid container spacing={isXs ? 1 : 2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={6} md={3}>
          <MetricsCard
            title="Σύνολο Πελατών"
            value={statistics.total_clients}
            color="primary"
          />
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <MetricsCard
            title="Συνολικό Πιστωτικό"
            value={formatCurrency(statistics.total_credit)}
            color="info"
          />
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <MetricsCard
            title="Εξοφλημένο"
            value={formatCurrency(statistics.total_paid)}
            color="success"
          />
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <MetricsCard
            title="Υπόλοιπο"
            value={formatCurrency(statistics.balance)}
            color="warning"
          />
        </Grid>
      </Grid>

      {/* Search and Filters */}
      <SearchHeader
        searchValue={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder="Αναζήτηση πελατών (όνομα, email, επιχείρηση, ΑΦΜ)..."
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilter}
        sortField={sortField}
        sortDirection={sortDirection}
        onSortChange={handleSort}
        sortOptions={[
          { value: 'name', label: 'Όνομα' },
          { value: 'email', label: 'Email' },
          { value: 'balance', label: 'Υπόλοιπο' },
          { value: 'total_credit', label: 'Πιστωτικό' },
          { value: 'services_count', label: 'Υπηρεσίες' },
        ]}
      />

      {/* Error Display */}
      {error && (
        <Box sx={{ mb: 2 }}>
          <Typography color="error">
            {typeof error === 'string' ? error : JSON.stringify(error)}
          </Typography>
        </Box>
      )}

      {/* Success Display */}
      {success && (
        <Box sx={{ mb: 2 }}>
          <Typography color="success.main">
            {success}
          </Typography>
        </Box>
      )}

      {/* Data Table */}
      <ResponsiveTable
        data={clients}
        columns={columns}
        loading={loading}
        emptyMessage="Δεν βρέθηκαν πελάτες"
        sortField={sortField}
        sortDirection={sortDirection}
        onSort={handleSort}
        renderMobileCard={renderClientCard}
      />

      {/* Pagination */}
      <PaginationComponent
        currentPage={currentPage}
        totalPages={Math.ceil(totalClients / itemsPerPage)}
        totalItems={totalClients}
        itemsPerPage={itemsPerPage}
        onPageChange={handlePageChange}
        onItemsPerPageChange={handleItemsPerPageChange}
      />

      {/* Create Modal */}
      <FormBuilder
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        schema={createClientSchema}
        onSubmit={handleCreateSubmit}
        modalProps={{
          title: 'Δημιουργία Νέου Πελάτη',
          maxWidth: 'md'
        }}
      />

      {/* Edit Modal */}
      <FormBuilder
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        schema={updateClientSchema}
        entity={selectedClient}
        onSubmit={handleEditSubmit}
        modalProps={{
          title: 'Επεξεργασία Πελάτη',
          maxWidth: 'md'
        }}
      />

      {/* Client Action Dialog */}
      <ClientActionDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
        client={selectedClient}
      />
    </Box>
  );
};

export default ClientListingPage;